import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Calendar } from "lucide-react";
import { Popover, TextField, MenuItem, Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import AirDatepicker from "air-datepicker";
import "air-datepicker/air-datepicker.css";
import "./air-datepicker-custom.css";
import "./custom-datepicker.scss";
import localeEn from "air-datepicker/locale/en";
import { customRanges, DEFAULT_TIME_RANGE, RANGE_TYPE_LABELS, TEXT_LIBRARY } from "../../utils/constants";
import { getDateRangeFormat, getRange, getMaxTime, getTimezoneLabel } from "../../utils/utility";
import { getLocalStorage, patchLocalStorage, LOCAL_STORAGE_KEYS } from "../../utils/localStorageUtil";

const CustomDatePicker = forwardRef(
  ({ onChange, isRangePicker = true, showCustom = true, maxAllowedDays = 2 }, ref) => {
    useImperativeHandle(ref, () => ({
      handleCustomChange: (e, setRange) => handleCustomChange(e, setRange),
    }));
    const [anchorEl, setAnchorEl] = useState(null);
    const [range, setRange] = useState(getRange(DEFAULT_TIME_RANGE));
    const [custom, setCustom] = useState(DEFAULT_TIME_RANGE);
    const [selectedDate, setSelectedDate] = useState(getRange(DEFAULT_TIME_RANGE));
    const [datepickerDiv, setDatepickerDiv] = useState(null);
    const datepickerInstanceRef = useRef(null);
    const [startTime, setStartTime] = useState(dayjs().subtract(12, "hour"));
    const [endTime, setEndTime] = useState(dayjs());

    // Today's date for maxDate
    const today = dayjs().endOf("day").toDate();

    useEffect(() => {
      try {
        const savedState = getLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE);

        if (savedState) {
          // 1. Restore the 'custom' dropdown type
          if (savedState.custom) {
            setCustom(savedState.custom);
          }

          // 2. Restore Start/End times for the TimePickers
          if (savedState.startTime) setStartTime(dayjs(savedState.startTime));
          if (savedState.endTime) setEndTime(dayjs(savedState.endTime));

          // 3. Restore the Range
          // If the user saved a "Custom" static range, we restore the specific dates.
          // If they saved a dynamic range (e.g., "Last 24 Hours"), we might prefer to
          // recalculate it relative to *now* (using getRange), or restore exactly what they saw.
          // Below logic restores exactly what was saved if it exists, otherwise falls back to defaults.
          if (Array.isArray(savedState.range) && savedState.range.length > 0) {
            const restoredRange = savedState.range.map(d => dayjs(d));
            setRange(restoredRange);
            setSelectedDate(restoredRange);

            // Trigger the parent's onChange so the app filters update immediately
            if (onChange) {
              onChange(restoredRange, savedState.custom);
            }
          } else if (savedState.custom && savedState.custom !== RANGE_TYPE_LABELS.CUSTOM) {
            // If we have a type but no static range dates, recalculate relative to now
            const newRange = getRange(savedState.custom);
            setRange(newRange);
            setSelectedDate(newRange);
            if (onChange) onChange(newRange, savedState.custom);
          }
        }
      } catch (error) {
        console.error("Failed to restore datepicker state", error);
      }
    }, []);
    useEffect(() => {
      if (!anchorEl) {
        setDatepickerDiv(null);
        if (datepickerInstanceRef.current) {
          datepickerInstanceRef.current.destroy();
          datepickerInstanceRef.current = null;
        }
        return;
      }
      const div = document.createElement("div");
      setDatepickerDiv(div);
      let dp;
      setTimeout(() => {
        dp = new AirDatepicker(div, {
          range: isRangePicker,
          multipleDatesSeparator: " - ",
          locale: localeEn,
          maxDate: today, // Block future dates
          toggleSelected: ({ datepicker }) => {
            if (datepicker.selectedDates.length > 1) {
              return true;
            }
            return false;
          },
          onSelect: ({ date, datepicker }) => {
            if (Array.isArray(date) && date.length === 2) {
              // when both start and end dates are selected
              const sortedDates = date.map(d => dayjs(d)).sort((a, b) => a - b);
              // Block future dates in range selection
              const endDate = sortedDates[1].endOf("day");
              const todayDayjs = dayjs(today);
              const validEndDate = endDate.isAfter(todayDayjs) ? todayDayjs : endDate;
              const dateRange = [sortedDates[0].startOf("day"), validEndDate];
              setSelectedDate(dateRange);
              setCustom(RANGE_TYPE_LABELS.CUSTOM);
              datepicker.update(
                {
                  minDate: "",
                  maxDate: today,
                  selectedDates: dateRange,
                },
                { silent: true }
              );
            } else if (Array.isArray(date) && date.length === 1) {
              // when only start date is selected
              const selectedDay = dayjs(date[0]);
              const startOfDay = selectedDay.startOf("day");
              setSelectedDate([startOfDay]);
              const minAllowed = selectedDay.subtract(maxAllowedDays, "day").toDate();
              let potentialMaxDate = selectedDay.add(maxAllowedDays, "day");
              if (potentialMaxDate > today) {
                potentialMaxDate = today;
              }
              const maxAllowed = potentialMaxDate;
              datepicker.update(
                {
                  minDate: minAllowed,
                  maxDate: maxAllowed,
                  selectedDates: startOfDay,
                },
                { silent: true }
              );
              setCustom(RANGE_TYPE_LABELS.CUSTOM);
            } else if (Array.isArray(date) && date.length === 0) {
              // when dates are cleared
              datepicker.update(
                {
                  minDate: "",
                  maxDate: today,
                  selectedDates: [],
                },
                { silent: true }
              );
            }
          },
          autoClose: false,
          selectedDates: Array.isArray(range) && range.length > 0 ? range.map(d => (d ? d.toDate() : d)) : [],
        });
        datepickerInstanceRef.current = dp;
      }, 0);
      return () => {
        if (datepickerInstanceRef.current && typeof datepickerInstanceRef.current.destroy === "function") {
          datepickerInstanceRef.current.destroy();
        }
        datepickerInstanceRef.current = null;
      };
    }, [anchorEl, isRangePicker]);

    const handleCustomChange = (e, setRangeBool = false) => {
      const value = e.target.value;
      setCustom(value);
      const newRange = getRange(value);
      setSelectedDate(newRange);
      if (setRangeBool) {
        setRange(newRange);
      }
      if (datepickerInstanceRef.current && value !== RANGE_TYPE_LABELS.CUSTOM) {
        const dp = datepickerInstanceRef.current;
        dp.selectDate([newRange[0].toDate(), newRange[1].toDate()], { silent: true });
      }
    };

    const handleConfirm = () => {
      let selectedDateRange = selectedDate;
      if (selectedDateRange?.length === 1) {
        selectedDateRange = [selectedDate[0].startOf("day"), selectedDate[0].endOf("day")];
        setSelectedDate(selectedDateRange);
        if (datepickerInstanceRef.current) {
          datepickerInstanceRef.current.update(
            {
              selectedDates: selectedDateRange,
            },
            { silent: true }
          );
        }
      }

      if (custom === RANGE_TYPE_LABELS.CUSTOM && selectedDateRange?.length === 2) {
        let alignedStart = selectedDateRange[0].hour(startTime.hour()).minute(startTime.minute());
        let alignedEnd = selectedDateRange[1].hour(endTime.hour()).minute(endTime.minute());

        // If start date is today
        const now = dayjs();
        if (selectedDateRange[0].isSame(now, "day")) {
          const twelveHoursBefore = now.subtract(12, "hour");
          // If 12 hours before is still today, set to that
          if (twelveHoursBefore.isSame(now, "day")) {
            alignedStart = selectedDateRange[0].hour(twelveHoursBefore.hour()).minute(twelveHoursBefore.minute());
          } else {
            // If going back 12 hours would cross into yesterday, force to 12:00 AM
            alignedStart = selectedDateRange[0].hour(0).minute(0);
          }
        }

        setStartTime(alignedStart);
        setEndTime(alignedEnd);
        selectedDateRange = [alignedStart, alignedEnd];
      }
      setRange(selectedDateRange);
      if (onChange) onChange(selectedDateRange, custom);
      try {
        patchLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE, {
          custom: custom,
          range: selectedDateRange.map(d => d.toISOString()),
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
        });
      } catch (error) {
        console.error("Failed to save datepicker state", error);
      }
      setAnchorEl(null);
    };

    const handleCancel = () => {
      setSelectedDate(range);
      setAnchorEl(null);
    };

    const popupContent = (
      <div className="p-6 min-w-[300px] flex flex-col gap-4">
        {datepickerDiv && <div ref={node => node && !node.hasChildNodes() && node.appendChild(datepickerDiv)} />}
        {/* NEW TIME PICKERS */}
        {custom === RANGE_TYPE_LABELS.CUSTOM && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="flex gap-4 mt-2">
              <TimePicker
                label={TEXT_LIBRARY.START_TIME}
                value={startTime}
                onChange={setStartTime}
                sx={{ width: 135 }}
                maxTime={getMaxTime(selectedDate[0])}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: false,
                    className: "custom-timepicker",
                  },
                }}
              />

              <TimePicker
                label={TEXT_LIBRARY.END_TIME}
                value={endTime}
                onChange={setEndTime}
                sx={{ width: 135 }}
                maxTime={getMaxTime(selectedDate[1] || selectedDate[0])}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: false,
                    className: "custom-timepicker",
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        )}
        {showCustom && (
          <TextField
            select
            value={custom}
            onChange={handleCustomChange}
            size="small"
            variant="outlined"
            className="mb-2 bg-white"
          >
            {customRanges
              .filter(opt => opt.noOfDays <= maxAllowedDays + 1)
              .map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.85rem" }}>
                  {opt.label}
                </MenuItem>
              ))}
          </TextField>
        )}
        <div className="flex justify-between gap-4 mt-1">
          <button
            className="w-1/2 bg-transparent text-black border border-gray-400 rounded-lg py-2 text-sm font-medium cursor-pointer"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="w-1/2 bg-orange-500 text-white border-none rounded-lg py-2 text-sm font-medium cursor-pointer"
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    );

    return (
      <>
        <div className="relative">
          <TextField
            className="w-full custom-datepicker-field"
            value={getDateRangeFormat(range)}
            style={{ width: "440px" }}
            size="small"
            onClick={e => setAnchorEl(e.currentTarget)}
            InputProps={{
              startAdornment: (
                <span className="text-gray-400 pointer-events-none w-9">
                  <Calendar size={18} />
                </span>
              ),
              endAdornment: (
                <Box
                  component="legend"
                  sx={{
                    width: "auto",
                    px: 0.5, // Padding to cut the border cleanly
                    fontSize: "0.75rem",
                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                    color: "text.secondary",
                    fontWeight: 400,
                    ml: 0.5,
                  }}
                >
                  {getTimezoneLabel()}
                </Box>
              ),
              readOnly: true,
            }}
          />
        </div>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCancel}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {popupContent}
        </Popover>
      </>
    );
  }
);

CustomDatePicker.displayName = "CustomDatePicker";
export default CustomDatePicker;

CustomDatePicker.propTypes = {
  onChange: PropTypes.func,
  isRangePicker: PropTypes.bool,
  showCustom: PropTypes.bool,
  maxAllowedDays: PropTypes.number,
};
